import React from "react";
import { Box, Typography } from "@mui/material";
import { ImageMetadata } from "../types";

interface GuidingAccuracyCardProps {
  imagesForView: ImageMetadata[];
}

const GuidingAccuracyCard: React.FC<GuidingAccuracyCardProps> = ({
  imagesForView,
}) => {
  // Only use light frames for guiding error calculations
  const lightFrames = imagesForView.filter((img) =>
    img.FrameType ? img.FrameType === "LIGHT" : !!img.FilterName
  );
  // Helper to check for valid numbers
  const isValidNumber = (val: any) => typeof val === "number" && isFinite(val);

  // Helper to filter outliers using mean and standard deviation
  const filterOutliers = (values: number[], threshold = 3) => {
    if (values.length < 2) return values;
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const std = Math.sqrt(
      values.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / values.length
    );
    return values.filter((v) => Math.abs(v - mean) <= threshold * std);
  };

  const validRA = lightFrames.filter((img) =>
    isValidNumber(img.GuidingRMSRAArcSec)
  );
  const validDEC = lightFrames.filter((img) =>
    isValidNumber(img.GuidingRMSDECArcSec)
  );

  // Filter outliers for RA and DEC
  const raValues = validRA.map((img) => img.GuidingRMSRAArcSec);
  const decValues = validDEC.map((img) => img.GuidingRMSDECArcSec);
  const filteredRA = filterOutliers(raValues);
  const filteredDEC = filterOutliers(decValues);

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        borderRadius: 3,
        p: 3,
        boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        minHeight: 220,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: "text.primary", mb: 2 }}
      >
        Average Guiding Accuracy
      </Typography>
      <Box sx={{ display: "flex", gap: 4 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            RA Guiding
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: "text.primary", fontWeight: 700 }}
          >
            {`${(
              filteredRA.reduce((sum, v) => sum + v, 0) /
              (filteredRA.length || 1)
            ).toFixed(2)}''`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            RA error (Arc-Sec)
          </Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            DEC Guiding
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: "text.primary", fontWeight: 700 }}
          >
            {`${(
              filteredDEC.reduce((sum, v) => sum + v, 0) /
              (filteredDEC.length || 1)
            ).toFixed(2)}''`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            DEC error (Arc-Sec)
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default GuidingAccuracyCard;
